import { useState, useRef, useEffect } from 'react';
import useEditorFileUpload from '../shared/hooks/useEditorFileUpload';

// Mirror the edit-page FileField accept list (TaskInfoPanel.yaml).
const ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip';
const UPLOAD_URL = '/decoupled-api/file/upload/node/task/field_attached_files';

/**
 * Deferred file upload for the ticket-creation modal. The ticket does not exist
 * yet, so each picked file is uploaded IMMEDIATELY (a TEMPORARY managed file —
 * returns a fid + a top-level deleteUrl); the parent collects `files` and sends
 * the fids as attachedFiles on save (they become permanent then), or deletes
 * them on cancel. Reproduces the edit page FileField UX (drag-drop, multiple,
 * preview, per-file error).
 *
 *   files    [{ fid, url, filename, filemime, deleteUrl }]
 *   setFiles React setter owned by the PARENT (single source of truth for the
 *            save payload + the cancel cleanup).
 */
export default function TaskFileUpload({ files = [], setFiles }) {
  const { uploadFile } = useEditorFileUpload(UPLOAD_URL, null);
  const [pending, setPending] = useState([]); // { id, name, error }
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  // Set true when the modal closes (this component unmounts) so an upload that
  // resolves AFTER a cancel deletes its (orphan) temporary file rather than
  // committing it — closes the cancel-mid-upload race.
  const cancelledRef = useRef(false);
  useEffect(() => () => { cancelledRef.current = true; }, []);

  const handleFiles = async (list) => {
    for (const file of Array.from(list || [])) {
      const id = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
      setPending((prev) => [...prev, { id, name: file.name, error: null }]);
      try {
        const res = await uploadFile(file);
        if (cancelledRef.current) {
          // Modal cancelled while this was uploading: don't commit; delete the orphan.
          if (res?.deleteUrl) fetch(res.deleteUrl, { method: 'POST', credentials: 'include' }).catch(() => {});
          return;
        }
        if (res?.fid) {
          setFiles((prev) => [...prev, { fid: res.fid, url: res.url, filename: res.filename || file.name, filemime: res.filemime, deleteUrl: res.deleteUrl }]);
        }
        setPending((prev) => prev.filter((p) => p.id !== id));
      } catch (err) {
        if (cancelledRef.current) return;
        setPending((prev) => prev.map((p) => (p.id === id ? { ...p, error: err?.message || "Échec de l'envoi" } : p)));
      }
    }
  };

  const remove = (fid) => {
    const f = files.find((x) => x.fid === fid);
    setFiles((prev) => prev.filter((x) => x.fid !== fid));
    // Best-effort delete of the now-orphan temporary file (CSRF auto-injected).
    if (f?.deleteUrl) fetch(f.deleteUrl, { method: 'POST', credentials: 'include' }).catch(() => {});
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer?.files); };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center gap-1 px-4 py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${dragOver ? 'border-iris-500 bg-iris-50' : 'border-slate-300 bg-white hover:border-slate-400'}`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
        <span className="text-sm text-slate-500">Glisser-déposer ou <span className="text-iris-600 font-medium">parcourir</span></span>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {(files.length > 0 || pending.length > 0) && (
        <ul className="mt-3 space-y-2">
          {files.map((f) => (
            <li key={f.fid} className="flex items-center gap-3 p-2 border border-slate-200 rounded-lg bg-white">
              {f.filemime?.startsWith('image/') && f.url
                ? <img src={f.url} alt={f.filename} className="w-9 h-9 rounded object-cover bg-slate-100" />
                : <span className="w-9 h-9 rounded bg-slate-100 flex items-center justify-center text-slate-400"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg></span>}
              <span className="flex-1 min-w-0 truncate text-sm text-slate-700">{f.filename}</span>
              <button type="button" onClick={() => remove(f.fid)} className="text-slate-400 hover:text-red-500 p-1" title="Retirer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </li>
          ))}
          {pending.map((p) => (
            <li key={p.id} className="flex items-center gap-3 p-2 border border-slate-200 rounded-lg bg-slate-50">
              {p.error
                ? <span className="w-9 h-9 rounded bg-red-50 flex items-center justify-center text-red-500 font-bold">!</span>
                : <svg className="animate-spin w-5 h-5 text-iris-500 ml-2" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
              <span className="flex-1 min-w-0 truncate text-sm text-slate-600">{p.name}</span>
              {p.error && <span className="text-xs text-red-500 truncate max-w-[45%]" title={p.error}>{p.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
